import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import useSlug from '../../../hooks/useSlug'
import sendToWs from '../../websocket/sendToWs'
import { fetchMeetingRoom, selectMeetingRoom } from '../meetingRoomSlice'
import { currentUserSelector } from '../../auth/authSlice'
import env from '../../../constants/env'
import { MeetingItem } from '../types'
import { User } from '../../auth/types'
import RouteLeavingPrompt from '../../../components/RouteLeavingPrompt'
import {_} from '../../../utils/localization'
import RecordingsButton from '../recording-components/RecordingsButton'

const S = {
    Wrapper: styled.div`
      height: calc(100% - 6px); //subtract a few pixels for the timetable
      width: 100%;
    `,
}

// Enhanced for Digital Samba Embedded API - uses JWT tokens and new URL structure
export const getSambaUrl = (
    eventSlug: string,
    meeting: MeetingItem,
    currentUser: User
) => {
    if (!meeting) return

    // New Digital Samba Embedded URL structure (priority order)
    // 1. Use friendly_url for cleaner URLs
    if (meeting.samba_token && meeting.samba_friendly_url) {
        return `${env.sambaUrl}/${meeting.samba_friendly_url}?token=${meeting.samba_token}`
    }

    // 2. Fallback to room ID if friendly_url not available but token exists
    if (meeting.samba_token && meeting.samba_room_id) {
        return `${env.sambaUrl}/${meeting.samba_room_id}?token=${meeting.samba_token}`
    }

    // 3. Legacy fallback - use samba_slug with token if available
    if (meeting.samba_token && meeting.samba_slug) {
        return `${env.sambaUrl}/${meeting.samba_slug}?token=${meeting.samba_token}`
    }

    // 4. Legacy user slug structure (for backward compatibility)
    if (meeting.samba_user_slug) {
        return `${env.sambaUrl}/invite/${meeting.samba_user_slug}`
    }

    // 5. Original legacy structure as final fallback
    if (meeting.samba_slug && meeting.samba_hash) {
        const params = new URLSearchParams({
            hash: meeting.samba_hash,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name
        }).toString()
        return `${env.sambaUrl}/live/${env.sambaUser}/${meeting.samba_slug}?${params}`
    }

    // No valid URL could be constructed
    console.warn('Unable to construct Samba URL - no valid tokens or slugs available', meeting)
    return undefined
}

const MeetingRoom = () => {
    const dispatch = useDispatch()
    const currentUser = useSelector(currentUserSelector())
    const eventSlug = useSlug('eventSlug')
    const meetingSpaceSlug = useSlug('meetingSlug')
    const roomSlug = useSlug('meetingRoomSlug')
    const meeting = useSelector(selectMeetingRoom)
    const [sambaUrl, setSambaUrl] = useState<string | undefined>('')

    const subscribe = (slug: string) => sendToWs({
        type: 'join_meeting_room',
        slug,
        space_id: meeting?.space_id,
    })

    const unsubscribe = (slug: string) => sendToWs({
        type: 'leave_meeting_room',
        slug,
        space_id: meeting?.space_id,
    })

    useEffect(() => {
        dispatch(fetchMeetingRoom({ eventSlug, meetingSlug: meetingSpaceSlug, roomSlug }))
    }, [dispatch, eventSlug, meetingSpaceSlug, roomSlug])

    useEffect(() => {
        setSambaUrl(getSambaUrl(eventSlug, meeting, currentUser))
    }, [currentUser, eventSlug, meeting, meetingSpaceSlug])

    useEffect(() => {
        if (meeting) {
            dispatch(subscribe(roomSlug))
            return () => {
                dispatch(unsubscribe(roomSlug))
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, meeting, roomSlug])

    // Don't render iframe if no valid URL is available
    if (!sambaUrl) {
        return (
            <S.Wrapper>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Unable to load meeting room. Please try again later.</p>
                    {process.env.NODE_ENV === 'development' && (
                        <pre>{JSON.stringify(meeting, null, 2)}</pre>
                    )}
                </div>
            </S.Wrapper>
        )
    }

    return (
        <>
            { env.enableRouteLeavePrompt && (
                <RouteLeavingPrompt
                    when
                    confirmText={_('videocall.confirm_leave_question')}
                    okButton={_('videocall.confirm_leave_button_yes')}
                    cancelButton={_('videocall.confirm_leave_button_no')}
                />
            )}

            <S.Wrapper>
                <iframe
                    src={sambaUrl}
                    title="Samba Meeting"
                    allow="microphone *; camera *; autoplay; display-capture;"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    // allowusermedia is added in the samba code, but TS gives an error. Doesn't seem necessary on Firefox
                />
                {
                    meeting && (
                        <RecordingsButton meeting={meeting} />
                    )
                }
            </S.Wrapper>
        </>
    )
}

export default MeetingRoom
